import { CountryCodesAlpha2 } from '@xing-com/crate-common-graphql-types';
import type {
  Maybe,
  PhoneNumber,
  XingIdContactDetailsData,
} from '@xing-com/crate-common-graphql-types/';
import {
  getCountryCodeFromPhoneNumber,
  getDialCodeFromCountryCode,
} from '@xing-com/crate-jobs-domain-apply-helpers';
import type {
  UserFormParsedPhoneData,
  UserFormPhoneData,
} from '@xing-com/crate-jobs-domain-apply-types';

const accountSettingsDataCountryByDialCode: Record<string, CountryCodesAlpha2> =
  {
    '41': CountryCodesAlpha2.Ch,
    '43': CountryCodesAlpha2.At,
    '49': CountryCodesAlpha2.De,
  };

// we're retrieving the full phone number (country code + phone number) from accountSettings where available country codes can be only DE (+49), AT (+43) or CH (+41)
const parsePhoneFromUserAccountData = (
  number: string
): UserFormParsedPhoneData => ({
  countryCode: accountSettingsDataCountryByDialCode[number.slice(1, 3)],
  number: number.slice(3),
});

const parsePhoneFromPreviousApplication = (
  number: string
): UserFormParsedPhoneData => {
  const normalizedNumber = number.replaceAll(' ', '');
  const countryCode = getCountryCodeFromPhoneNumber(
    normalizedNumber
  ) as CountryCodesAlpha2;
  const dialCode =
    countryCode && getDialCodeFromCountryCode(countryCode).replaceAll(' ', '');

  return {
    countryCode,
    number: dialCode
      ? normalizedNumber.slice(dialCode.length + 1)
      : normalizedNumber,
  };
};

const PLUS_SIGN_IN_COUNTRY_CODE = 1;

const parsePhoneFromPrivateContactDetails = (
  privatePhoneNumber: PhoneNumber
): UserFormParsedPhoneData | null => {
  const countryDialingCode =
    privatePhoneNumber?.countryCode &&
    getDialCodeFromCountryCode(privatePhoneNumber.countryCode);

  if (
    privatePhoneNumber?.countryCode &&
    countryDialingCode &&
    privatePhoneNumber.phoneNumber
  ) {
    const phoneNumber = privatePhoneNumber.phoneNumber.slice(
      countryDialingCode.length + PLUS_SIGN_IN_COUNTRY_CODE
    );

    return { countryCode: privatePhoneNumber.countryCode, number: phoneNumber };
  }

  return null;
};

export const getUserPhoneData = (
  accountSettingsPhone: string | undefined | null,
  privateContactDetailsPhone: XingIdContactDetailsData | undefined | null,
  previousApplicationPhone?: string | undefined | null
): Maybe<UserFormPhoneData> => {
  const parsedPreviousApplicationPhone =
    previousApplicationPhone &&
    parsePhoneFromPreviousApplication(previousApplicationPhone);

  if (
    parsedPreviousApplicationPhone &&
    parsedPreviousApplicationPhone.countryCode
  ) {
    return {
      fullNumber: previousApplicationPhone,
      parsedNumber: parsedPreviousApplicationPhone,
    };
  }

  const parsedAccountSettingsPhone =
    accountSettingsPhone && parsePhoneFromUserAccountData(accountSettingsPhone);

  if (parsedAccountSettingsPhone && parsedAccountSettingsPhone.countryCode) {
    return {
      fullNumber: accountSettingsPhone,
      parsedNumber: parsedAccountSettingsPhone,
    };
  }

  const privateContactDetails = privateContactDetailsPhone?.mobile
    ? privateContactDetailsPhone?.mobile
    : privateContactDetailsPhone?.phone;

  return privateContactDetails
    ? {
        fullNumber: privateContactDetails.phoneNumber,
        parsedNumber: parsePhoneFromPrivateContactDetails(
          privateContactDetails
        ),
      }
    : null;
};
